/**
 * Поле нового комментария или ответа: текст с оформлением и, если
 * разрешено, файлы. Редактор тот же, что у описания задачи.
 *
 * Файлы копятся до отправки: загрузить их можно только к уже созданному
 * комментарию, поэтому этим занимается вызывающий после onSubmit.
 * Текст необязателен, если приложены файлы: картинку отправляют и без подписи.
 * Ctrl+Enter (⌘+Enter) отправляет — кроме блока кода и цитаты, где это
 * выход из блока.
 */

import { useRef, useState } from "react";

import { Button } from "./Button";
import { AttachButton, PendingFiles } from "./PendingFiles";
import { isEmptyDoc, MAX_COMMENT_CHARS, plainText, type RichDoc } from "@taskflow/client-core";
import { RichTextEditor } from "./richtext/lazy";
import { type AttachOptions, usePendingFiles } from "./usePendingFiles";

interface CommentComposerProps {
  /** true — отправлено, поле очищается. body — текст с оформлением. */
  onSubmit: (text: string, files: File[], body: RichDoc | null) => Promise<boolean> | boolean;
  onCancel?: () => void;
  attach?: AttachOptions;
  placeholder: string;
  ariaLabel?: string;
  submitLabel?: string;
  autoFocus?: boolean;
}

export function CommentComposer({
  onSubmit,
  onCancel,
  attach,
  placeholder,
  ariaLabel,
  submitLabel = "Отправить",
  autoFocus = false,
}: CommentComposerProps) {
  const [doc, setDoc] = useState<RichDoc | null>(null);
  // Новый ключ — новое пустое поле после отправки.
  const [generation, setGeneration] = useState(0);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const tray = usePendingFiles(attach);

  const empty = isEmptyDoc(doc);
  const tooLong = doc !== null && plainText(doc).length > MAX_COMMENT_CHARS;

  const submit = async () => {
    if (sendingRef.current || tooLong) return;
    if (empty && tray.files.length === 0) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const body = empty ? null : doc;
      if (await onSubmit(plainText(body).trim(), tray.files, body)) {
        setDoc(null);
        setGeneration((value) => value + 1);
        tray.clear();
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <RichTextEditor
        key={generation}
        value={null}
        onChange={setDoc}
        ariaLabel={ariaLabel ?? placeholder}
        placeholder={placeholder}
        autoFocus={autoFocus}
        size="sm"
        maxChars={MAX_COMMENT_CHARS}
        disabled={sending}
        onSubmit={() => {
          void submit();
          return true;
        }}
        {...(attach === undefined ? {} : { onPasteFiles: (files: File[]) => tray.add(files) })}
      />

      <PendingFiles
        files={tray.files}
        label="Файлы к отправке"
        disabled={sending}
        onRemove={tray.remove}
      />

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          loading={sending}
          disabled={(empty && tray.files.length === 0) || tooLong}
          onClick={() => void submit()}
        >
          {submitLabel}
        </Button>
        {onCancel && (
          <Button size="sm" variant="secondary" disabled={sending} onClick={onCancel}>
            Отмена
          </Button>
        )}
        {attach && <AttachButton attach={attach} disabled={sending} onPick={tray.add} />}
      </div>
    </div>
  );
}
